import * as React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { Chat } from "../components/chat";
import { useChat } from "../components/chat/hooks/useChat";
import { getChatInfo } from "../services/api";

type LocationState = {
  fromNewToCreated?: boolean;
  lastMessage?: string;
  short_answer?: boolean;
  use_pro_model?: boolean;
};

type ChatPageInstanceProps = {
  routeChatId?: string;
};

const inFlightChatPreferences = new Map<
  string,
  Promise<Awaited<ReturnType<typeof getChatInfo>>>
>();

function getOrCreateChatPreferencesRequest(chatId: string) {
  const existing = inFlightChatPreferences.get(chatId);
  if (existing) return existing;
  const created = getChatInfo(chatId).finally(() => {
    inFlightChatPreferences.delete(chatId);
  });
  inFlightChatPreferences.set(chatId, created);
  return created;
}

const ChatPageInstance: React.FC<ChatPageInstanceProps> = ({
  routeChatId: id,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [routeSwitchLoading, setRouteSwitchLoading] = React.useState(false);
  const [isRouteHydrated, setIsRouteHydrated] = React.useState(false);
  /** Id della chat per cui abbiamo già eseguito il flusso new→created (stream avviato); evita loadChat dopo navigate. */
  const didHandleFromNewToCreatedRef = React.useRef<string | null>(null);

  const state = (location.state ?? null) as LocationState | null;
  let fromNewToCreated = Boolean(state?.fromNewToCreated);
  const lastMessageFromState =
    typeof state?.lastMessage === "string" ? state.lastMessage : undefined;
  let isFirstMessageJustSend = false;

  const {
    sessionId,
    messages,
    feedbackMap,
    isLoadingMessage,
    isLoadingChat,
    transientEvents,
    sendMessage,
    loadChat,
    newChat,
    streamResponseForMessage,
    handleFeedback,
    handleRetry,
    editingMessageId,
    editingContent,
    setEditingContent,
    handleStartEdit,
    handleSendFromEdit,
    cancelEdit,
    resumeStreaming,
    shortAnswer,
    useProModel,
    hydrateChatPreferences,
    persistShortAnswer,
    persistUseProModel,
  } = useChat("chunks", {
    fromNewToCreated,
    initialUserMessage: lastMessageFromState,
  });

  const isNewRoute = id === "new";

  const shouldShowImmediateLoading = Boolean(
    id && id !== "new" && !fromNewToCreated
  );

  React.useLayoutEffect(() => {
    if (shouldShowImmediateLoading) {
      setRouteSwitchLoading(true);
    }
  }, [id, shouldShowImmediateLoading]);

  React.useEffect(() => {
    setIsRouteHydrated(false);
  }, [id]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!id || id === "new") {
        hydrateChatPreferences({ short_answer: false, use_pro_model: true });
        newChat();
        if (!cancelled) {
          setRouteSwitchLoading(false);
          setIsRouteHydrated(true);
        }
        return;
      }
      if (fromNewToCreated) {
        if (isFirstMessageJustSend) return;
        isFirstMessageJustSend = true;
        didHandleFromNewToCreatedRef.current = id;
        setRouteSwitchLoading(false);
        setIsRouteHydrated(true);
        hydrateChatPreferences({
          short_answer: state?.short_answer ?? false,
          use_pro_model: state?.use_pro_model ?? false,
        });
        void streamResponseForMessage(id, lastMessageFromState ?? "");
        navigate(`/chat/${id}`, { replace: true, state: {} });
        return;
      }
      if (didHandleFromNewToCreatedRef.current === id) {
        didHandleFromNewToCreatedRef.current = null;
        if (!cancelled) {
          setRouteSwitchLoading(false);
          setIsRouteHydrated(true);
        }
        return;
      }
      await loadChat(id, true);
      if (!cancelled) {
        setRouteSwitchLoading(false);
        setIsRouteHydrated(true);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    id,
    isNewRoute,
    fromNewToCreated,
    lastMessageFromState,
    loadChat,
    newChat,
    navigate,
    streamResponseForMessage,
    hydrateChatPreferences,
  ]);

  React.useEffect(() => {
    let cancelled = false;
    const loadChatInfo = async () => {
      if (!isRouteHydrated) return;
      if (!id || id === "new" || isNewRoute) {
        hydrateChatPreferences({ short_answer: false, use_pro_model: true });
        return;
      }
      try {
        const info = await getOrCreateChatPreferencesRequest(id);
        if (cancelled) return;
        hydrateChatPreferences({
          short_answer: Boolean(info.short_answer),
          use_pro_model: Boolean(info.use_pro_model),
        });

        if (info?.has_streaming) {
          void resumeStreaming(id, info.last_message ?? undefined);
        }
      } catch {
        if (cancelled) return;
        hydrateChatPreferences({ short_answer: false, use_pro_model: true });
      }
    };
    void loadChatInfo();
    return () => {
      cancelled = true;
    };
  }, [id, isNewRoute, isRouteHydrated, resumeStreaming, hydrateChatPreferences]);

  // Convert ChatMessage[] to Message[]
  const chatMessages = React.useMemo(() => {
    return messages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      documents: msg.documents,
      isStreaming: msg.isStreaming,
      disableEdit: msg.disableEdit,
      isModelUnavailable: msg.isModelUnavailable,
      timestamp: msg.timestamp,
      responseTime: msg.responseTime,
      responseMeta: msg.responseMeta,
    }));
  }, [messages]);

  if (routeSwitchLoading || isLoadingChat) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Chat
      key={"chat-" + id}
      messages={chatMessages}
      isLoading={isLoadingMessage}
      transientEvents={transientEvents}
      onSendMessage={sendMessage}
      sessionId={sessionId ?? undefined}
      feedbackMap={feedbackMap}
      onFeedback={handleFeedback}
      onRetry={handleRetry}
      editingMessageId={editingMessageId ?? undefined}
      editingContent={editingContent}
      onEditingContentChange={setEditingContent}
      onStartEdit={handleStartEdit}
      onSendFromEdit={handleSendFromEdit}
      onCancelEdit={cancelEdit}
      shortAnswer={shortAnswer}
      useProModel={useProModel}
      onChangeShortAnswer={persistShortAnswer}
      onChangeUseProModel={persistUseProModel}
    />
  );
};

export const ChatPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <ChatPageInstance key={`chat-page-${id}`} routeChatId={id} />;
};
